import { useMemo, useState } from 'react'
import {
  BindingContext,
  bindingsByVariable,
  canonicalBindingFor,
  resolveBinding,
  variablesIn,
} from './bindings'
import {
  bindingSourceEnum,
  bindingSourceFields,
  TemplateVariableBinding,
  WhatsappTemplate,
} from './models'

/** A variable's chosen source and (for entity sources) field. */
export type DraftBinding = { source: string; field: string }

function toDraftBindings(bindings: TemplateVariableBinding[]): Record<string, DraftBinding> {
  const draft: Record<string, DraftBinding> = {}
  bindings.forEach((binding) => {
    draft[binding.variable] = { source: binding.source, field: binding.field ?? '' }
  })
  return draft
}

/**
 * Shared authoring state for a template's `{{variable}}` -> data-source
 * bindings, keyed by variable name. Used by both the create and edit
 * (rejected-template-edit-resubmit) forms so they stay a single
 * implementation.
 */
export function useTemplateBindings(body: string, initialBindings: TemplateVariableBinding[] = []) {
  const variables = useMemo(() => variablesIn(body), [body])
  const [bindings, setBindings] = useState<Record<string, DraftBinding>>(() =>
    toDraftBindings(initialBindings),
  )

  // A canonical token (e.g. `reservation.checkin_date`) carries its own binding —
  // typed freehand or inserted by the picker, it's bound the moment it appears in
  // the body, without a separate manual-mapping step. An explicit source/field
  // choice recorded in `bindings` state always wins over the derived default.
  const effectiveBindings = useMemo(() => {
    const merged = { ...bindings }
    variables.forEach((variable) => {
      if (merged[variable]) return
      const canonical = canonicalBindingFor(variable)
      if (canonical) merged[variable] = canonical
    })
    return merged
  }, [variables, bindings])

  const setSource = (variable: string, source: string) => {
    // Default an entity source to its first field; Manual carries no field.
    const field =
      source in bindingSourceFields
        ? bindingSourceFields[source as keyof typeof bindingSourceFields][0]
        : ''
    setBindings((prev) => ({ ...prev, [variable]: { source, field } }))
  }
  const setField = (variable: string, field: string) => {
    // Falls back to the canonical default (not just `prev[variable]`) so editing
    // the field of a variable that's only canonically bound so far doesn't drop
    // its source.
    setBindings((prev) => ({
      ...prev,
      [variable]: { ...(prev[variable] ?? canonicalBindingFor(variable)), field },
    }))
  }

  const isMapped = (variable: string) => {
    const binding = effectiveBindings[variable]
    if (!binding?.source) return false
    if (binding.source === bindingSourceEnum.MANUAL) return true
    return Boolean(binding.field)
  }
  const allMapped = variables.every(isMapped)

  // Unmapped variables are left out — a draft may be saved with them; submit
  // is gated on `allMapped`, so a submitted payload always carries every one.
  const buildBindings = (): TemplateVariableBinding[] =>
    variables.filter(isMapped).map((variable) => {
      const binding = effectiveBindings[variable]
      return binding.source === bindingSourceEnum.MANUAL
        ? { variable, source: binding.source, field: null }
        : { variable, source: binding.source, field: binding.field }
    })

  return {
    variables,
    bindings: effectiveBindings,
    setSource,
    setField,
    isMapped,
    allMapped,
    buildBindings,
  }
}

/**
 * Shared send-time resolution of a template's variables, used by both the
 * in-conversation template composer and the new-conversation panel. Bound
 * variables resolve from `context`; a manual binding, or one that could not
 * resolve (e.g. no linked reservation), is "prompted" — the sender fills it in.
 * Only prompted values go to the server, which resolves the rest itself and uses
 * these as the fallback for a binding it cannot resolve.
 */
export function useTemplateSendValues(
  template: WhatsappTemplate | null,
  context: BindingContext,
  orgName: string,
) {
  const [overrides, setOverrides] = useState<Record<string, string>>({})

  const variables = useMemo(() => (template ? variablesIn(template.body) : []), [template])
  const bindings = useMemo(
    () => bindingsByVariable(template?.variableBindings),
    [template?.variableBindings],
  )

  // Memoized so the returned values stay referentially stable between renders
  // (callers pass them into callbacks' dependency lists).
  const { promptedVars, values, hasEveryValue, manualValues } = useMemo(() => {
    const resolved: Record<string, string> = {}
    variables.forEach((name) => {
      const value = resolveBinding(bindings[name], context, orgName, template?.language)
      if (value !== undefined) resolved[name] = value
    })
    const prompted = variables.filter((name) => resolved[name] === undefined)
    const valueFor = (name: string) => resolved[name] ?? overrides[name] ?? ''
    const allValues = Object.fromEntries(variables.map((name) => [name, valueFor(name)]))
    return {
      promptedVars: prompted,
      values: allValues,
      hasEveryValue: variables.every((name) => allValues[name].trim().length > 0),
      manualValues: Object.fromEntries(prompted.map((name) => [name, valueFor(name)])),
    }
  }, [variables, bindings, context, orgName, template?.language, overrides])

  const setOverride = (name: string, value: string) =>
    setOverrides((prev) => ({ ...prev, [name]: value }))

  return { bindings, promptedVars, overrides, setOverride, values, hasEveryValue, manualValues }
}
